import { DescriptionsMap, RecommendationMap } from "./HealthReportMaps";
import SceneObjectUtils from './SceneObjectUtils';
import TextUtils from "./TextUtils";
import UIUtils from "./UIUtils";
import QuizManager from '../QuizManager';
import store, { StoreActions } from "../../state/store";
import Scenario from "../scenarioElements/Scenario";
import { InsetTypes, ApplicationModuleTypes, TrackerTypes, CustomEventTypes, ChecklistFormType } from "../../globals";
import { HealthReportId, HealthReportType, ObjectTriggerTypes, ProjectLinkModes, VisibilityModes } from "../../interfaces/enums";
import { HealthReport } from "../../interfaces/HealthReport";
import Engine from '../Engine';
import UITemplateTypes from '../../components/uisystem/templates/types';
import ARTracker from '../ARTracker';
import { Trigger } from "../../interfaces/Trigger";
import DefaultSceneManager from '../DefaultSceneManager';
import SetObjectNameAction from "../../actions/SetObjectNameAction";
import { IChecklist } from "../../interfaces/checklistItem";
import AssetsManager from "../AssetsManager";

export default class ScenarioHealthUtils
{
	//#region Internal Maps
	static EventsMap = {
		[CustomEventTypes.updateTriggerComponent] : ScenarioHealthUtils.CheckSceneObjectTriggersEventHandler,
		[CustomEventTypes.updatePropertiesComponent] : ScenarioHealthUtils.CheckSceneObjectsPropertiesEventHandler,
		[CustomEventTypes.objectNameChanged] : ScenarioHealthUtils.ObjectsNameChangedEventHandler,
	}
	
	static StepChecksMap = [
		ScenarioHealthUtils.GatherSceneObjectsInformation,
		ScenarioHealthUtils.CheckInsetsReferences,
		ScenarioHealthUtils.CheckSceneObjects,
		ScenarioHealthUtils.CheckObjectTriggersForStep,
		ScenarioHealthUtils.CheckCustomMaterialsForStep,		
		ScenarioHealthUtils.CheckUISystemUsageForStep,
	]
	
	static GlobalChecksMap = [
		ScenarioHealthUtils.CheckAssetLibrary,
		ScenarioHealthUtils.CheckForQuizResults,
		ScenarioHealthUtils.CheckTrackerReferences,
	]
	//#endregion

	static GenerateReport(id : HealthReportId, args : string[] = [], targetWindow : string, stepId  = 'global', value : any | null = null, targetObject : any | null  = null, targetAsset = '')
	{
		if(targetObject)
		{
			args.push(targetObject.name);
		}
		if(value)
		{
			args.push(value.toString());
		}

		const newReport = 
		{ 
			id: id,
			type: id > 5000 ? HealthReportType.Error : HealthReportType.Warning,
			description: DescriptionsMap[id](args), 
			recommendation: RecommendationMap[id](args),
			targetWindow: targetWindow, 
			step: stepId, 
			value: value,
			targetObject: targetObject?.userData?.guid ?? '', 
			targetAsset: targetAsset 
		};

		
		return newReport;
	}

	static StepReportsExists(stepId : string)
	{
		const reports = store.getState().scenarioModule.healthReports;
		if(reports[stepId] && reports[stepId].length > 0) return true;
		return false;
	}

	static AddReport(newReport : HealthReport)
	{
		if(!ScenarioHealthUtils.StepReportsExists(newReport.step))
		{
			store.dispatch(StoreActions.scenario.addReport(newReport));
		}
		else
		{
			const reports = store.getState().scenarioModule.healthReports[newReport.step];
			if(reports)
			{
				let duplicateReportFound = false;
				for(const report of reports)
				{
					if(ScenarioHealthUtils.CompareReports(report, newReport))
					{
						duplicateReportFound = true;
						break;
					}
				}
				if(!duplicateReportFound) store.dispatch(StoreActions.scenario.addReport(newReport));
			}
			else
			{
				store.dispatch(StoreActions.scenario.addReport(newReport));
			}
		}
	}

	static RemoveReport(report : HealthReport | null)
	{
		if(report === null) return;
		
		const stepId = report.step;
		if(ScenarioHealthUtils.StepReportsExists(stepId))
		{
			const reports = store.getState().scenarioModule.healthReports[stepId];
			if(reports)
			{
				for(let i = 0; i < reports.length; i++)
				{
					if(ScenarioHealthUtils.CompareReports(reports[i], report))
					{
						store.dispatch(StoreActions.scenario.removeReportAt({report: report, index: i}));
						break;
					}
				}
			}
		}
	}

	static FindReportInStep(stepId  = 'global', reportId : HealthReportId, targetObject : any | null = null, value : any | null = null)
	{
		if(ScenarioHealthUtils.StepReportsExists(stepId))
		{
			const reports = store.getState().scenarioModule.healthReports[stepId];
			if(reports)
			{
				for(const report of reports)
				{
					if(report.id === reportId)
					{
						if(targetObject)
						{
							if(report.targetObject === targetObject.userData.guid)
							{
								if(value)
								{
									if(report.value === value) return report;
								}
								else
								{
									return report;
								}
							}
						}
						else
						{
							if(value)
							{
								if(report.value === value) return report;
							}
							else
							{
								return report;
							}
						}
					}
				}
			}
		}

		return null;
	}

	static FindReportGlobally(reportId : HealthReportId, targetObject : any | null = null, value : any | null = null)
	{
		const reports : Record<string, HealthReport[]> = store.getState().scenarioModule.healthReports;
		let foundReport: HealthReport | null = null;

		for(const stepId of Object.keys(reports))
		{
			foundReport = ScenarioHealthUtils.FindReportInStep(stepId, reportId, targetObject, value);
			if(foundReport) break;
			
		}

		return foundReport;
	}

	static FindReportsByObjectId(objectId : string)
	{
		const reports : Record<string, HealthReport[]> = store.getState().scenarioModule.healthReports;
		const foundReports : HealthReport[] = [];

		for(const stepReports of Object.values(reports))
		{
			if(stepReports)
			{
				for(const report of stepReports)
				{
					if(report.targetObject === objectId ) foundReports.push(report);
				}
			}
		}

		return foundReports;
	}

	static getReportsOfType(type : HealthReportType)
	{
		const reports : Record<string, HealthReport[]> = store.getState().scenarioModule.healthReports;
		const generatedList : HealthReport[] = [];
		for(const reportList of Object.values(reports))
		{
			for(const report of reportList)
			{
				if(report.type === type)
				{
					generatedList.push(report);
				}
			}
		}
		return generatedList;
	}

	static hasReports()
	{
		const reports : Record<string, HealthReport[]> = store.getState().scenarioModule.healthReports;
		if(Object.values(reports).length > 0) return true;
		return false;
	}

	static CompareReports(left : HealthReport, right : HealthReport)
	{
		if(left.step !== right.step) return false;
		if(left.targetWindow !== right.targetWindow) return false;
		if(left.targetAsset !== right.targetAsset) return false;
		if(left.targetObject !== right.targetObject) return false;
		if(left.id !== right.id) return false;
		if(left.type !== right.type) return false;
		if(left.value !== right.value) return false;
		return true;
	}

	static ClearReports()
	{
		store.dispatch(StoreActions.scenario.clearReports());
		ScenarioHealthUtils.SceneStats.clear();
		ScenarioHealthUtils.ObjectsWithCustomMaterial.clear();
	}

	static ObjectsNameChangedEventHandler(event)
	{
		if(event.detail?.sender && event.detail.sender instanceof SetObjectNameAction)
		{
			if(event.detail.objects && event.detail.objects.length > 0)
			{
				for(let i = 0; i < event.detail.objects?.length; i++)
				{
					const object = event.detail.objects[i];
					const oldName = event.detail.oldNames[i];
					const newName = event.detail.names[i];
					ScenarioHealthUtils.UpdateObjectName(object.userData.guid, oldName, newName);
				}
			}
		}
	}

	static UpdateObjectName(objectId : string, oldName : string, newName : string)
	{
		const reports = ScenarioHealthUtils.FindReportsByObjectId(objectId);
		for(const report of reports)
		{
			const reportCopy = {...report};
			ScenarioHealthUtils.RemoveReport(report);
			reportCopy.description = reportCopy.description.replace(oldName, newName);
			ScenarioHealthUtils.AddReport(reportCopy);
		}
	}

	static init()
	{
		ScenarioHealthUtils.RegisterEventListeners();
	}

	static RegisterEventListeners()
	{
		for(const [event, func] of Object.entries(ScenarioHealthUtils.EventsMap))
		{
			document.addEventListener(event, func);
		}
	}

	static FullHealthCheck()
	{
		ScenarioHealthUtils.ClearReports();

		//global checks
		for(const globalCheck of ScenarioHealthUtils.GlobalChecksMap)
		{
			globalCheck();
		}

		//step specific checks
		for(const sequence of Scenario.getSequences())
		{
			ScenarioHealthUtils.CheckStep(sequence.defaultStep);		
			for(const step of sequence.steps)
			{
				ScenarioHealthUtils.CheckStep(step);
			}
		}
	}

	static CheckStep(step : any)
	{
		for(const check of ScenarioHealthUtils.StepChecksMap)
		{
			check(step);
		}
	}

	static RemoveStepReports(step : any)
	{
		const reports = [...store.getState().scenarioModule.healthReports[step.id] ?? []];

		if(reports && reports.length > 0)
		{
			for(const report of reports)
			{
				ScenarioHealthUtils.RemoveReport(report);
			}
		}
		
	}

	static SceneStats : Map<string, number[]> = new Map();
	static ObjectsWithCustomMaterial : Map<string, Set<any>> = new Map();
	static ObjectsWithTriggers : Map<string, Set<any>> = new Map();

	//single traverse per scene to gather needed information for health checks
	static GatherSceneObjectsInformation(step = Scenario.currentStep())
	{
		if(!step) return;

		const selectionRoot = Engine.getInstance().getSelectionRoot(step.scene.userData.guid);
		
		let triangleCount = 0; let objectCount = 0;

		if (selectionRoot)
		{
			selectionRoot.traverse((child) =>
			{
				triangleCount += SceneObjectUtils.getObjectTriangleCount(child);
				if (child !== selectionRoot)
				{
					if (!child.userData.hideInHierarchy)
					{
						objectCount++;

						ScenarioHealthUtils.AddObjectWithCustomMaterial(step, child);
						ScenarioHealthUtils.AddObjectWithTriggers(step, child);
					}
				}
			});
		}

		ScenarioHealthUtils.SceneStats.set(step.scene.userData.guid, [Math.ceil(triangleCount), objectCount]);
	}

	static AddObjectWithCustomMaterial(step, object)
	{
		if(!step) return;
		
		if(object.userData?.visibility?.device === VisibilityModes.Custom || object.userData?.visibility?.standalone === VisibilityModes.Custom)
		{
			if(!ScenarioHealthUtils.ObjectsWithCustomMaterial.has(step.id))
			{
				ScenarioHealthUtils.ObjectsWithCustomMaterial.set(step.id, new Set());
			}

			if(!ScenarioHealthUtils.ObjectsWithCustomMaterial.get(step.id)?.has(object))
			{
				ScenarioHealthUtils.ObjectsWithCustomMaterial.get(step.id)?.add(object);
			}

			ScenarioHealthUtils.CheckCustomMaterialsForObject(object, step);
		}
	}

	static RemoveObjectWithCustomMaterial(step, object)
	{
		if(!step) return;

		if(ScenarioHealthUtils.ObjectsWithCustomMaterial.has(step.id))
		{
			if(ScenarioHealthUtils.ObjectsWithCustomMaterial.get(step.id)?.has(object))
			{
				ScenarioHealthUtils.ObjectsWithCustomMaterial.get(step.id)?.delete(object);
			}
		}
	}

	static AddObjectWithTriggers(step, object)
	{
		if(!step) return;

		if(object.userData?.triggers && object.userData?.triggers?.length > 0)
		{
			let allTriggersRemoved = true;
			for(const trigger of object.userData.triggers)
			{
				if(!trigger.removedSyncedTrigger)
				{
					allTriggersRemoved = false;
					break;
				}
			}

			if(allTriggersRemoved) return;

			if(!ScenarioHealthUtils.ObjectsWithTriggers.has(step.id))
			{
				ScenarioHealthUtils.ObjectsWithTriggers.set(step.id, new Set());
			}

			if(!ScenarioHealthUtils.ObjectsWithTriggers.get(step.id)?.has(object))
			{
				ScenarioHealthUtils.ObjectsWithTriggers.get(step.id)?.add(object);
				ScenarioHealthUtils.CheckTriggersForObject(object, step);
			}
		}
	}

	static RemoveObjectWithTriggers(step, object)
	{
		if(!step) return;

		if(ScenarioHealthUtils.ObjectsWithTriggers.has(step.id))
		{
			if(ScenarioHealthUtils.ObjectsWithTriggers.get(step.id)?.has(object))
			{
				ScenarioHealthUtils.ObjectsWithTriggers.get(step.id)?.delete(object);
			}
		}
	}

	static CheckInsetsReferences(step = Scenario.currentStep())
	{
		if(!step) return;

		const reports : HealthReport[] = []
		const ui : any = store.getState().uiModule.ui[step.id];
		const possibleTemplates = ['Content', 'Custom'];
		if(!ui || !possibleTemplates.includes(ui.active) || !ui[ui.active]) return [];

		const insets : any = ui[ui.active].Insets;
		if(!insets) return [];

		let i = 1;
		while(Object.keys(insets).includes('inset ' + i + ' type'))
		{
			//extract each inset data
			const insetType = insets['inset ' + i + ' type'].value;
			const insetCamera = insets['inset ' + i + ' camera'].value;
			const insetAudio = insets['inset ' + i + ' audio'].value;
			const insetImage = insets['inset ' + i + ' image'].value;
			const insetVideo = insets['inset ' + i + ' video'].value;
			const insetUrl = insets['inset ' + i + ' url'].value;
			const insetStep = insets['inset ' + i + ' step'].value;

			ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.InsetReference, null, i));
			ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.InsetDisabled, null, i));
			ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.InsetImageReference, null, i));
			ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.InvalidUrl, null, i));

			let report : HealthReport | null = null;
			switch(insetType)
			{
				case InsetTypes.INVISIBLE:
					if(insetAudio || insetImage || insetVideo || insetUrl)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetDisabled, undefined, ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
				case InsetTypes.IMAGE:
					if(!insetImage)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetReference, [insetType], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
				case InsetTypes.VIDEO:
					
					if(!insetVideo || !AssetsManager.getAssetInfo(insetVideo))
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetReference, [insetType], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					else if(!insetImage)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetImageReference, [], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
				case InsetTypes.URL:
					if(!insetUrl)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetReference, [insetType], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					else if(!insetImage)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetImageReference, [], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					else if(!TextUtils.isValidURL(insetUrl))
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InvalidUrl, [], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
				case InsetTypes.AUDIO:
					if(!insetAudio || !AssetsManager.getAssetInfo(insetAudio))
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetReference, [insetType], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					else if(!insetImage)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetImageReference, [], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
				case InsetTypes.CAMERA:
					if(!insetCamera)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetReference, [insetType], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
				case InsetTypes.STEP:
					if(!insetStep)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetReference, [insetType], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					else if(!insetImage)
					{
						report = ScenarioHealthUtils.GenerateReport(HealthReportId.InsetImageReference, [], ApplicationModuleTypes.UIEditor, step.id, i);
						ScenarioHealthUtils.AddReport(report);
					}
					break;
			}
			i++;
		}

		return reports;
	}

	static CheckAssetLibrary()
	{
		const usedAssets : any = AssetsManager.getAssetsInProject();

		let totalAssetsSize = 0;
		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(undefined, HealthReportId.AssetsInUseSize));

		function addAssetSizeToTotal(assetId : string)
		{
			const assetInfo = AssetsManager.getAssetInfo(assetId);
			if(assetInfo)
			{
				const assetSize = AssetsManager.getAssetSize(assetInfo);
				if(assetSize)
				{
					totalAssetsSize += assetSize;
				}
			}
		}

		for(const assetType of Object.keys(usedAssets))
		{
			for(const assetId of usedAssets[assetType])
			{
				addAssetSizeToTotal(assetId);
			}
		}

		if(totalAssetsSize > 210000000)
		{
			const report = ScenarioHealthUtils.GenerateReport(HealthReportId.AssetsInUseSize, undefined, ApplicationModuleTypes.AssetLibrary, undefined, totalAssetsSize);
			ScenarioHealthUtils.AddReport(report);
		}
	}

	static ObjectAdded(scene : any, object : any, objectCount  = 0, triangleCount  = 0)
	{
		ScenarioHealthUtils.UpdateSceneStats(scene, objectCount, triangleCount);
		ScenarioHealthUtils.AddObjectWithCustomMaterial(Scenario.getStepBySceneId(scene.userData.guid), object);
		ScenarioHealthUtils.AddObjectWithTriggers(Scenario.getStepBySceneId(scene.userData.guid), object);
		ScenarioHealthUtils.CheckTrackerReferences();
	}

	static ObjectRemoved(scene : any, object : any, objectCount  = 0, triangleCount  = 0)
	{
		ScenarioHealthUtils.UpdateSceneStats(scene, objectCount, triangleCount);
		ScenarioHealthUtils.RemoveObjectWithCustomMaterial(Scenario.getStepBySceneId(scene.userData.guid), object);
		ScenarioHealthUtils.RemoveObjectWithTriggers(Scenario.getStepBySceneId(scene.userData.guid), object);

		const objectReports = [...ScenarioHealthUtils.FindReportsByObjectId(object.userData.guid)];
		for(const report of objectReports)
		{
			ScenarioHealthUtils.RemoveReport(report);
		}
	}

	static UpdateSceneStats(scene : any, objectCount : number, triangleCount : number)
	{
		if(!scene) return;
		
		if(!ScenarioHealthUtils.SceneStats.has[scene.userData.guid])
		{
			const selectionRoot = Engine.getInstance().getSelectionRoot(scene.userData.guid);
			ScenarioHealthUtils.SceneStats.set(scene.userData.guid, SceneObjectUtils.calculateHierarchyStats(selectionRoot));
		}
		else
		{
			const newStats = ScenarioHealthUtils.SceneStats.get(scene.userData.guid);
			if(newStats)
			{
				newStats[0] += objectCount;
				newStats[1] += triangleCount;
				ScenarioHealthUtils.SceneStats.set(scene.userData.guid, newStats);
			}
		}

		const step = Scenario.getStepBySceneId(scene.userData.guid);
		if(step)
		{
			ScenarioHealthUtils.CheckSceneObjects(step);
		}
	}

	static CheckSceneObjects(step = Scenario.currentStep())
	{
		if(!step || !step.scene) return;

		let sceneStats = ScenarioHealthUtils.SceneStats.get(step.scene.userData.guid);
		if(!sceneStats)
		{
			const selectionRoot = Engine.getInstance().getSelectionRoot(step.scene.userData.guid);
			ScenarioHealthUtils.SceneStats.set(step.scene.userData.guid, SceneObjectUtils.calculateHierarchyStats(selectionRoot));
			sceneStats = ScenarioHealthUtils.SceneStats.get(step.scene.userData.guid);
		}
		
		if(!sceneStats) return;

		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.StepTriangleCount));
		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.StepObjectCount));

		let defaultStepTriangleCountReport: HealthReport | null = null;
		let defaultStepObjectCountReport: HealthReport | null = null;

		if(step.connectedToDefault || step.isTrackersStep())
		{
			const defaultStep = step.sequence.defaultStep;
			defaultStepTriangleCountReport = ScenarioHealthUtils.FindReportInStep(defaultStep.id, HealthReportId.StepTriangleCount);
			defaultStepObjectCountReport = ScenarioHealthUtils.FindReportInStep(defaultStep.id, HealthReportId.StepObjectCount);
		}

		if(sceneStats[0] > 2000000 && !defaultStepTriangleCountReport)
		{
			const report = ScenarioHealthUtils.GenerateReport(HealthReportId.StepTriangleCount, undefined, ApplicationModuleTypes.StepHierarchy, step.id, sceneStats[0]);
			ScenarioHealthUtils.AddReport(report);
		}

		if(sceneStats[1] > 10000 && !defaultStepObjectCountReport)
		{
			const report = ScenarioHealthUtils.GenerateReport(HealthReportId.StepObjectCount, undefined, ApplicationModuleTypes.StepHierarchy, step.id, sceneStats[1]);
			ScenarioHealthUtils.AddReport(report);
		}
	}

	static CheckForQuizResults()
	{
		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportGlobally(HealthReportId.MissingGradedQuestions));

		const stepWithQuizResults = UIUtils.findStepWithUiTemplate(UITemplateTypes.QUIZ_RESULTS)
		if(stepWithQuizResults)
		{
			if(!QuizManager.areThereAnyGradedQuestions())
			{
				const report = ScenarioHealthUtils.GenerateReport(HealthReportId.MissingGradedQuestions, undefined, ApplicationModuleTypes.UIEditor, stepWithQuizResults.id);
				ScenarioHealthUtils.AddReport(report);
			}
		}

		if(!Scenario.current()?.sequences) return;
		for(const sequence of Scenario.getSequences())
		{
			for(const step of sequence.steps)
			{
				ScenarioHealthUtils.CheckForQuizQuestions(step);
			}
		}
	}

	static CheckForQuizQuestions(step : any)
	{
		const stepId = step?.id;
		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(stepId, HealthReportId.MissingQuizResultTemplate));

		let areThereAnyGradedQuestions = false;
		const stepChecklist : IChecklist = store.getState().checklistModule.checklist[stepId];
		if(!stepChecklist || stepChecklist.type !== ChecklistFormType.QUIZ) return;

		const questions = stepChecklist.questions;
		if(questions && questions.length > 0)
		{
			for(const question of questions)
			{
				if(question.score! > 0)
				{
					areThereAnyGradedQuestions = true;
				}
			}
		}

		if(areThereAnyGradedQuestions)
		{
			const stepWithQuizResults = UIUtils.findStepWithUiTemplate(UITemplateTypes.QUIZ_RESULTS)
			if(!stepWithQuizResults)
			{
				const report = ScenarioHealthUtils.GenerateReport(HealthReportId.MissingQuizResultTemplate, undefined, ApplicationModuleTypes.FormEditor, stepId);
				ScenarioHealthUtils.AddReport(report);
			}
		}
	}

	static CheckTrackerReferences()
	{
		const trackerList = ARTracker.getTrackerList();

		if(!trackerList || trackerList.length === 0 || !Scenario.getCPS()) return;

		for(const tracker of trackerList)
		{
			if(tracker.trackerType)
			{
				const cps = Scenario.getCPS();
				if ( cps ) 
				{
					ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(cps.id, HealthReportId.ARTrackerReference, tracker));
					switch(tracker.trackerType)
					{
						case TrackerTypes.IMAGE:
							if(!tracker.imageAssetId)
							{
								ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.ARTrackerReference, ['image'], ApplicationModuleTypes.ObjectProperties, cps.id, null, tracker));
							}
							break;
						case TrackerTypes.MODEL:
							if(!tracker.modelAssetId)
							{
								ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.ARTrackerReference, ['model'], ApplicationModuleTypes.ObjectProperties, cps.id, null, tracker));
							}
							break;
					}
				}
			}
		}
	}

	static CheckSceneObjectTriggersEventHandler()
	{
		ScenarioHealthUtils.CheckObjectTriggersForStep();
	}

	static CheckSceneObjectsPropertiesEventHandler()
	{
		ScenarioHealthUtils.CheckObjectTriggersForStep();
		ScenarioHealthUtils.CheckCustomMaterialsForStep()
	}

	static RemoveObjectTriggersReports(objectId : string)
	{
		const objectReports = ScenarioHealthUtils.FindReportsByObjectId(objectId);
		for(const objectReport of objectReports)
		{
			if(objectReport.description.includes('action is missing a'))
			{
				ScenarioHealthUtils.RemoveReport(objectReport);
			}
			if(objectReport.id === HealthReportId.InvalidUrl)
			{
				ScenarioHealthUtils.RemoveReport(objectReport);
			}
		}
	}

	static CheckObjectTriggersForStep(step = Scenario.currentStep())
	{
		if(!step) return;

		const objectsWithTriggers = ScenarioHealthUtils.ObjectsWithTriggers.get(step.id);
		if(objectsWithTriggers)
		{
			for(const object of objectsWithTriggers)
			{
				ScenarioHealthUtils.CheckTriggersForObject(object, step);
			}
		}
	}

	static CheckTriggersForObject(object : any, step : any)
	{
		if(!object || !step) return;
		
		ScenarioHealthUtils.AddObjectWithTriggers(step, object);

		const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid);
		if(defaultObject)
		{
			ScenarioHealthUtils.AddObjectWithTriggers(step.sequence.defaultStep, defaultObject);
			if(ScenarioHealthUtils.FindReportInStep(step.sequence.defaultStep.id, HealthReportId.ObjectTriggersReference, defaultObject))
			{
				//if the default object has a problem, don't check the object
				return;
			}
		}

		ScenarioHealthUtils.RemoveObjectTriggersReports(object.userData.guid);
		let newReportCreated = false;
		for(const trigger of object.userData.triggers ?? [])
		{
			if(trigger)
			{
				if(!ScenarioHealthUtils.CheckTrigger(trigger, step.id, object))
				{
					newReportCreated = true;
					if(step.isDefault())
					{
						//if default step has a problem, remove all other warnings for this object from other steps
						const linkedObjects = DefaultSceneManager.getInstance().getLinkedObjects(object);
						for(const linkedObject of linkedObjects)
						{
							ScenarioHealthUtils.RemoveObjectTriggersReports(linkedObject.userData.guid);
						}
					}
				}
			}
		}

		if(!newReportCreated && step.isDefault())
		{
			//if we just finished checking the default object and everything is ok, check the linked objects
			for(const s of step.sequence.steps)
			{
				ScenarioHealthUtils.CheckObjectTriggersForStep(s);
			}
		}
	}

	static CheckTrigger(trigger : Trigger, stepId : string, targetObject : any, windowId : string = ApplicationModuleTypes.ObjectProperties)
	{
		if(trigger.removedSyncedTrigger) return true;

		let isTriggerValid = true;

		switch(trigger.type)
		{
			case ObjectTriggerTypes.MoveToStep:
				if(targetObject)
				{
					if(trigger.targetStep && trigger.targetStep === Scenario.getCPS()?.id) trigger.targetStep = '';
					if(!trigger.targetStep)
					{
						ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.ObjectTriggersReference, ['step'], windowId, stepId, null, targetObject));
						isTriggerValid = false;
					}
				}
				//extend after these for actions that aren't tied to a target object
				break;
			case ObjectTriggerTypes.URL:
				if(targetObject)
				{
					if(!trigger.url)
					{
						ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.ObjectTriggersReference, ['url'], windowId, stepId, null, targetObject));
						isTriggerValid = false;
					}
					else if(!TextUtils.isValidURL(trigger.url))
					{
						ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.InvalidUrl, ['url'], windowId, stepId, null, targetObject));
						isTriggerValid = false;
					}
				}
				break;
			case ObjectTriggerTypes.ProjectLink:
				if(targetObject)
				{
					const projectLinkMode : ProjectLinkModes = trigger.projectLinkMode;
					if((projectLinkMode === ProjectLinkModes.Online && !trigger.onlineScenarioName) ||
						(projectLinkMode === ProjectLinkModes.Offline && !trigger.offlineScenarioName)
					)
					{
						ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.ObjectTriggersReference, ['scenario link'], windowId, stepId, null, targetObject));
						isTriggerValid = false;
					}
				}
				break;
			case ObjectTriggerTypes.CalloutToggle:
			case ObjectTriggerTypes.VisibilityToggle:
				if(targetObject)
				{
					if(!trigger.targetObject)
					{
						ScenarioHealthUtils.AddReport(ScenarioHealthUtils.GenerateReport(HealthReportId.ObjectTriggersReference, ['object'], windowId, stepId, null, targetObject));
						isTriggerValid = false;
					}
				}
				break;
		}

		return isTriggerValid;
	}
		
	static CheckCustomMaterialsForStep(step : any = Scenario.currentStep())
	{
		if(!step || !ScenarioHealthUtils.ObjectsWithCustomMaterial.has(step.id)) return;
		for(const object of ScenarioHealthUtils.ObjectsWithCustomMaterial.get(step.id) ?? [])
		{
			ScenarioHealthUtils.CheckCustomMaterialsForObject(object, step);
		}
	}

	static CheckCustomMaterialsForObject(object : any, step : any)
	{
		if(!object || !step) return;

		const description = object.name + "'s Custom Material is missing a material reference";

		const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(object.userData.guid);
		if(defaultObject)
		{
			ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.sequence.defaultStep.id, HealthReportId.CustomMaterialReference, defaultObject));

			if((defaultObject.userData.visibility.device === VisibilityModes.Custom && !defaultObject.userData.visibility.customDeviceMaterial) ||
			(defaultObject.userData.visibility.standalone === VisibilityModes.Custom && !defaultObject.userData.visibility.customStandaloneMaterial))
			{
				//missing material reference on the default object
				const report = ScenarioHealthUtils.GenerateReport(HealthReportId.CustomMaterialReference, [], ApplicationModuleTypes.ObjectProperties, step.sequence.defaultStep.id, null, defaultObject);
				ScenarioHealthUtils.AddReport(report);
				return;
			}
		}

		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.CustomMaterialReference, object));

		if((object.userData.visibility.device === VisibilityModes.Custom && !object.userData.visibility.customDeviceMaterial) ||
			(object.userData.visibility.standalone === VisibilityModes.Custom && !object.userData.visibility.customStandaloneMaterial))
		{
			//missing material reference on the step object
			if(step.isDefault())
			{
				//if default step has a problem, remove all other warnings for this object from other steps
				for(const s of step.sequence.steps)
				{
					ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(s.id, HealthReportId.CustomMaterialReference, object));
				}
			}

			const report = ScenarioHealthUtils.GenerateReport(HealthReportId.CustomMaterialReference, [], ApplicationModuleTypes.ObjectProperties, step.id, null, object);
			ScenarioHealthUtils.AddReport(report);
		}
		else
		{
			//if we were in default step and just got it fixed, check all other steps for unattachmenets and restore their warnings
			if(step.isDefault())
			{
				for(const s of step.sequence.steps)
				{
					ScenarioHealthUtils.CheckCustomMaterialsForStep(s);
				}
			}
		}
	}

	static CheckUISystemUsageForStep(step = Scenario.currentStep())
	{
		if(!step) return;

		// clear old report
		ScenarioHealthUtils.RemoveReport(ScenarioHealthUtils.FindReportInStep(step.id, HealthReportId.UIUsingBothSystems));

		if(!step.uiTemplateId) return; // in case step is missing, or step has no new ui system template, it's impossible that both systems are used at once

		// if step has new ui system template, check if old ui system is used
		const oldStepUI = store.getState().uiModule.ui[step.id]?.active; // "active" is the name of the template used in old ui system
		if(!oldStepUI) return; // if old ui system is not used, it's impossible that both systems are used at once

		// if both systems are used at once, generate a warning
		const report = ScenarioHealthUtils.GenerateReport(HealthReportId.UIUsingBothSystems, [], ApplicationModuleTypes.UIEditor, step.id);
		ScenarioHealthUtils.AddReport(report);
	}
}